import { Routes } from '@angular/router';
import { BlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { AuthGuard } from './core/guards/auth.guard';
import { MsalRedirectComponent } from './msal-redirect/msal-redirect.component';
import { AppSideLoginComponent } from './pages/authentication/side-login/side-login.component';
import { PagesDashboardComponent } from './pages/pages-dashboard.component';
import { Page403Component } from './pages/authentication/page-403/page-403.component';
import { KpiGuard } from './core/guards/kpi.guard';
import { KycGuard } from './core/guards/kyc.guard';
import { ExecutionGuard } from './core/guards/execution.guard';
import { ExecutionRouteGuard } from './core/guards/execution-route.guard';
import { PaymentGuard } from './core/guards/payment.guard';
import { PaymentRouteGuard } from './core/guards/payment-route.guard';
import { AdminGuard } from './core/guards/admin.guard';
import { ClientSpaceGuard } from './core/guards/client-space.guard';
import { CorporateGuard } from './core/guards/corporate.guard';
import { ContactGuard } from './core/guards/contact.guard';
import { CrmGuard } from './core/guards/crm.guard';
import { BillingGuard } from './core/guards/billing.guard';
import { MaintenanceGuard } from './core/guards/maintenance.guard';
import { AccountSettingComponent } from './pages/admin/Account-profile/account-setting/account-setting.component';
import { NotificationTestComponent } from './pages/admin/notification-test/notification-test.component';
import { SiteMaintenanceComponent } from './pages/maintenance/site-maintenance.component';
import { ArturRedirectComponent } from './artur-redirect.component';
import { SuperAdminGuard } from './core/guards/super-admin.guard';

export const routes: Routes = [
  {
    path: 'artur',
    component: ArturRedirectComponent,
    canActivate: [AuthGuard, SuperAdminGuard],
  },
  {
    // Full-tab ONLYOFFICE editor, opened from the deal documents workspace.
    // Outside the Full layout on purpose: no header/sidenav over the toolbar.
    path: 'ged-editor/:documentId',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/ged-editor/ged-editor-page.component').then(
        (m) => m.GedEditorPageComponent,
      ),
  },
  {
    path: '',
    component: FullComponent,
    canActivate: [AuthGuard, MaintenanceGuard],
    canActivateChild: [MaintenanceGuard],
    children: [
      {
        path: '',
        redirectTo: 'pages-dashboard',
        pathMatch: 'full',
      },
      {
        path: 'pages-dashboard',
        component: PagesDashboardComponent,
      },
      {
        path: 'account-setting',
        component: AccountSettingComponent,
        data: {
          title: 'Account setting',
          breadcrumb: false,
        },
      },
      {
        path: 'notification-test',
        component: NotificationTestComponent,
        data: {
          title: 'Test Notifications',
          breadcrumb: false,
        },
      },
      {
        path: 'it-support',
        loadComponent: () =>
          import('./pages/support/it-support/it-support.component').then(
            (m) => m.ItSupportComponent
          ),
        data: {
          title: 'IT Support',
          breadcrumb: false,
        },
      },
      {
        path: 'faq',
        loadComponent: () =>
          import('./pages/support/faq/faq-page.component').then((m) => m.FaqPageComponent),
        data: {
          title: 'FAQ',
          breadcrumb: false,
        },
      },
      {
        path: 'opportunity-lifecycle',
        loadComponent: () =>
          import('./pages/support/opportunity-lifecycle/opportunity-lifecycle.component').then(
            (m) => m.OpportunityLifecycleComponent,
          ),
        data: {
          title: 'Opportunity lifecycle',
          breadcrumb: false,
        },
      },
      {
        path: 'data-register',
        loadComponent: () =>
          import('./pages/support/data-register/data-register.component').then(
            (m) => m.DataRegisterComponent,
          ),
        data: {
          title: 'Data register',
          breadcrumb: false,
        },
      },
      {
        path: 'crm',
        loadChildren: () =>
          import('./pages/crm/crm.routes').then((m) => m.CrmRoutes),
        canActivate: [CrmGuard],
      },
      {
        path: 'billing',
        loadChildren: () =>
          import('./pages/billing/billing.routes').then((m) => m.BillingRoutes),
        canActivate: [BillingGuard],
        canActivateChild: [BillingGuard],
      },
      {
        path: 'kyc',
        loadChildren: () =>
          import('./pages/kyc/kyc.routes').then((m) => m.KycRoutes),
        canActivate: [KycGuard],
      },
      {
        path: 'execution',
        loadChildren: () =>
          import('./pages/execution/execution.routes').then((m) => m.ExecutionRoutes),
        canActivate: [ExecutionGuard],
        canActivateChild: [ExecutionRouteGuard],
      },
      {
        path: 'payment',
        loadChildren: () =>
          import('./pages/payment/payment.routes').then((m) => m.PaymentRoutes),
        canActivate: [PaymentGuard],
        canActivateChild: [PaymentRouteGuard],
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./pages/admin/admin.routes').then((m) => m.AdminRoutes),
        canActivate: [AdminGuard],
      },
      {
        // CSA back-office for the Client Space (AetherConnect) product. The client
        // portal itself is a separate application on its own domain — this is only
        // the ArturPro-side administration of spaces, corporates and accesses.
        path: 'admin-client-space',
        loadChildren: () =>
          import('./pages/client-space/client-space.routes').then((m) => m.ClientSpaceRoutes),
        canActivate: [ClientSpaceGuard],
      },
      {
        path: 'corporate',
        loadChildren: () =>
          import('./pages/corporate/corporate.routes').then((m) => m.CorporateRoutes),
        canActivate: [CorporateGuard],
      },
      {
        path: 'contact',
        loadChildren: () =>
          import('./pages/contact/contact.routes').then((m) => m.ContactRoutes),
        canActivate: [ContactGuard],
      },
      {
        path: 'kpi',
        loadChildren: () =>
          import('./pages/kpi/kpi.routes').then((m) => m.KpiRoutes),
        canActivate: [KpiGuard],
      },
      {
        // Open to every signed-in user: no module guard.
        path: 'tools',
        loadChildren: () =>
          import('./pages/tools/tools.routes').then((m) => m.ToolsRoutes),
      },
      {
        path: 'starter',
        loadChildren: () =>
          import('./pages/pages.routes').then((m) => m.PagesRoutes),
      },
    ],
  },

  {
    path: '',
    component: BlankComponent,
    children: [
      {
        path: 'authentication',
        loadChildren: () =>
          import('./pages/authentication/authentication.routes').then(
            (m) => m.AuthenticationRoutes
          ),
      },
      {
        path: 'maintenance',
        component: SiteMaintenanceComponent,
      },
    ],
  },
  { path: 'verify-auth-redirect-user', component: MsalRedirectComponent },
  { path: 'login', component: AppSideLoginComponent },
  //{ path: 'login', component: AppBoxedLoginComponent}
  {
    path: 'page-403',
    component: BlankComponent,
    children: [
      {
        path: '',
        component: Page403Component,
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'authentication/error',
  },
];
